/**
 * Konvektiv issiqlik berish koeffitsientlari (film koeffitsientlari).
 *
 * MANBA: Kasatkin A.G., "Osnovnye protsessy i apparaty ximicheskoy texnologii";
 *        Pavlov, Romankov, Noskov, "Primery i zadachi po kursu PAXT".
 * Bu PAJ fanining asosiy darsliklari — talaba shu formulalar bilan hisoblaydi.
 *
 * QUVUR ICHIDA (majburiy oqim):
 *   Re ≥ 10⁴        Nu = 0.021 · Re^0.8 · Pr^0.43 · (Pr/Prw)^0.25 · ε_l
 *   2300 < Re < 10⁴ Nu — Re bo'yicha chiziqli interpolyatsiya Nu(2300) va Nu_turb(10⁴) orasida
 *                   (Gnielinski uslubi). Kasatkinning 0.008·Re^0.9·Pr^0.43 formulasi Re = 10⁴ da
 *                   ~5% uzilish beradi; dinamik modelda bu chegara yaqinida U "sakrab" tebranishga
 *                   olib keldi (1-lab testida aniqlangan), shuning uchun uzluksiz variant tanlandi.
 *   Re ≤ 2300       Nu = 3.66                                     (laminar, rivojlangan,
 *                                                                  devor T = const)
 * QOBIQ TOMONI (segmentli to'siqlar, tutam ko'ndalang yuvilishi):
 *   Re ≥ 1250       Nu = 0.24 · Re^0.6 · Pr^0.36 · (Pr/Prw)^0.25
 *   Re ≤ 800        Nu = 0.56 · Re^0.5 · Pr^0.36 · (Pr/Prw)^0.25
 *   800 < Re < 1250 ikki formula orasida ln(Re) bo'yicha silliq o'tish (Kasatkin formulalari
 *                   Re = 1000 da 17% uzilishga ega).
 *   Re va Nu tashqi diametr do bo'yicha, tezlik eng tor kesimda.
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR (kimyo texnologi tekshirishi kerak):
 *   1. (Pr/Prw)^0.25 = 1 — devor va oqim haroratlari farqi hisobga olinmagan.
 *   2. ε_l = 1 — L/d > 50 deb qabul qilingan (qisqa quvurlar uchun tuzatish yo'q).
 *   3. Laminar rejimda Kasatkinning Gr o'z ichiga olgan formulasi o'rniga
 *      Nu = 3.66 (erkin konveksiya e'tiborga olinmagan). Ogohlantirish beriladi.
 *   4. Qobiq tomonida to'siqlar orqali sizish (leakage, bypass) hisobga olinmagan.
 *   5. O'tish rejimlari interpolyatsiya bilan — darslik formulasidan farq qiladi (yuqorida).
 */
import { warn, type WarningSink } from '../../types';
import { density, prandtl, thermalConductivity, viscosity } from '../../properties';
import { shellFlowArea, tubeFlowArea } from './geometry';
import type { FilmCoefficient, HeatExchangerParams, Stream } from './types';

export function nusseltTubeSide(
  Re: number,
  Pr: number,
): { Nu: number; regime: FilmCoefficient['regime'] } {
  const turb = (re: number): number => 0.021 * Math.pow(re, 0.8) * Math.pow(Pr, 0.43);
  if (Re >= 1e4) return { Nu: turb(Re), regime: 'turbulent' };
  if (Re > 2300) {
    const g = (Re - 2300) / (1e4 - 2300);
    return { Nu: 3.66 + g * (turb(1e4) - 3.66), regime: 'transitional' };
  }
  return { Nu: 3.66, regime: 'laminar' };
}

const SHELL_RE_LO = 800;
const SHELL_RE_HI = 1250;

export function nusseltShellSide(
  Re: number,
  Pr: number,
): { Nu: number; regime: FilmCoefficient['regime'] } {
  const turb = (re: number): number => 0.24 * Math.pow(re, 0.6) * Math.pow(Pr, 0.36);
  const lam = (re: number): number => 0.56 * Math.pow(re, 0.5) * Math.pow(Pr, 0.36);
  if (Re >= SHELL_RE_HI) return { Nu: turb(Re), regime: 'turbulent' };
  if (Re <= SHELL_RE_LO) return { Nu: lam(Re), regime: 'laminar' };
  const g = Math.log(Re / SHELL_RE_LO) / Math.log(SHELL_RE_HI / SHELL_RE_LO);
  return { Nu: lam(Re) * (1 - g) + turb(Re) * g, regime: 'transitional' };
}

/**
 * Quvur ichidagi film koeffitsienti. Xossalar o'rtacha (bulk) haroratda.
 */
export function tubeSideFilm(
  p: HeatExchangerParams,
  stream: Stream,
  Tbulk: number,
  warnings?: WarningSink,
): FilmCoefficient {
  const d = p.tubes.innerDiameter;
  const rho = density(stream.substance, stream.phase, Tbulk, stream.pressure, warnings);
  const mu = viscosity(stream.substance, stream.phase, Tbulk, warnings);
  const lambda = thermalConductivity(stream.substance, stream.phase, Tbulk, warnings);
  const Pr = prandtl(stream.substance, stream.phase, Tbulk, warnings);

  const velocity = stream.massFlow / (rho * tubeFlowArea(p));
  const Re = (rho * velocity * d) / mu;
  const { Nu, regime } = nusseltTubeSide(Re, Pr);
  if (regime === 'laminar') warn(warnings, 'hx.laminarTubeSide', 'warning', { Re: Math.round(Re) });
  return { h: (Nu * lambda) / d, Re, Pr, Nu, velocity, regime };
}

/**
 * Qobiq tomon film koeffitsienti (segmentli to'siqlar).
 */
export function shellSideFilm(
  p: HeatExchangerParams,
  stream: Stream,
  Tbulk: number,
  warnings?: WarningSink,
): FilmCoefficient {
  const d = p.tubes.outerDiameter;
  const rho = density(stream.substance, stream.phase, Tbulk, stream.pressure, warnings);
  const mu = viscosity(stream.substance, stream.phase, Tbulk, warnings);
  const lambda = thermalConductivity(stream.substance, stream.phase, Tbulk, warnings);
  const Pr = prandtl(stream.substance, stream.phase, Tbulk, warnings);

  const velocity = stream.massFlow / (rho * shellFlowArea(p));
  const Re = (rho * velocity * d) / mu;
  const { Nu, regime } = nusseltShellSide(Re, Pr);
  if (regime === 'laminar') warn(warnings, 'hx.laminarShellSide', 'info', { Re: Math.round(Re) });
  return { h: (Nu * lambda) / d, Re, Pr, Nu, velocity, regime };
}

/**
 * Umumiy koeffitsient, tashqi yuzaga nisbatan:
 *   1/U = 1/ho + Rfo + Rw + (do/di)·(Rfi + 1/hi)
 */
export function overallCoefficient(
  p: HeatExchangerParams,
  hInner: number,
  hOuter: number,
  wallRes: number,
): number {
  const ratio = p.tubes.outerDiameter / p.tubes.innerDiameter;
  const rInv =
    1 / hOuter + (p.foulingOuter ?? 0) + wallRes + ratio * ((p.foulingInner ?? 0) + 1 / hInner);
  return 1 / rInv;
}
